export const getCsrfToken = () => {
  return document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content');
};

export const buildRequestOptions = (
  method,
  data,
  additionalHeaders = {},
  additionalOptions = {},
  useMultipartFormData = false
) => {
  const options = {
    ...additionalOptions,
    method: method.toUpperCase(),
    headers: {
      'X-CSRF-TOKEN': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
      ...additionalHeaders
    }
  };

  if (useMultipartFormData) {
    const formData = new FormData();

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach(item => {
            formData.append(
              `${key}[]`,
              !(item instanceof File) && typeof item === 'object'
                ? JSON.stringify(item)
                : item
            );
          });
        } else {
          formData.append(key, data[key]);
        }
      }
    }

    options.body = formData;
  } else {
    options.headers['Content-Type'] = 'application/json';

    if (data) {
      options.body = typeof data === 'string' ? data : JSON.stringify(data);
    }
  }

  return options;
};

const makeRequest = async (
  url,
  method = 'get',
  data = undefined,
  additionalHeaders = {},
  additionalOptions = {},
  useMultipartFormData = false
) => {
  const options = buildRequestOptions(
    method,
    data,
    additionalHeaders,
    additionalOptions,
    useMultipartFormData
  );

  return fetch(url, options).then(response => {
    if (!response.ok) {
      throw new Error(response.status);
    }

    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      return response.json();
    } else if (contentType && contentType.includes('text/plain')) {
      return response.text();
    } else {
      return response;
    }
  });
};

const parsingDefaults = {
  url: '',
  onStart: response => null,
  onData: response => null,
  onFinish: () => null,
  onParams: response => null,
  method: 'get',
  data: {},
  headers: {},
  signal: null
};

const parseStreamingData = async (params = parsingDefaults) => {
  const mergedParams = {
    ...parsingDefaults,
    ...params
  };

  const {
    url,
    onStart,
    onData,
    onFinish,
    onParams,
    method,
    data,
    headers,
    signal
  } = mergedParams;

  const fetchOptions = {
    method: method.toUpperCase(),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
      ...headers
    },
    signal: signal
  };

  if (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT') {
    fetchOptions.body = JSON.stringify(data);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 204) {
    return;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let dataBuffer = '';

  if (typeof onStart === 'function') onStart();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    dataBuffer += chunk;

    // Split the buffer into lines
    const lines = dataBuffer.split('\n');

    for (const line of lines) {
      if (!line) {
        continue;
      }

      if (line.startsWith('data:')) {
        const dataString = line.slice(5).trim();

        if (dataString.startsWith('{"error"')) {
          throw new Error(`Error received: ${dataString}`);
        }

        try {
          const jsonData = JSON.parse(dataString);

          if (typeof onData === 'function') onData(jsonData);
        } catch (error) {
          console.error('Failed to parse JSON:', error);
        }
      } else if (line.startsWith('params:')) {
        const dataString = line.slice(7).trim();

        try {
          const jsonData = JSON.parse(dataString);

          if (typeof onParams === 'function') onParams(jsonData);
        } catch (error) {
          console.error('Failed to parse JSON:', error);
        }
      }
    }

    dataBuffer = lines[lines.length - 1];
  }

  if (typeof onFinish === 'function') onFinish();
};

export { makeRequest, parseStreamingData };
